/**
 * Campaign work in the field app (N-PAC, 27 Oct 2026): for surveyors given the duties रैली मैनेजमेंट,
 * प्रत्याशी भ्रमण मैनेजमेंट and प्रचार गाड़ी मैनेजमेंट (admins see all three). Online: these records are
 * the whole assembly's, so they are read and saved straight from the API. Deleting stays on the web.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { RALLY_CHECKLIST, RALLY_KINDS, RALLY_STATUS, TOUR_PURPOSES, TRIP_STATUS, type RallyKind, type RallyStatus, type TourPurpose, type TourStatus, type TripStatus } from "@npac/rules";
import { apiErrorText } from "@core/messages";
import type { Rally, TourStop, Trip, Vehicle } from "../field/api";
import { parseAssemblyLink, parseDriveLink } from "../field/link";
import { useApp } from "../ui/context";
import { href, type CampaignScreen } from "../ui/router";
import { Banner, Button, Choice, Field, NumberField, PhoneField, Toggle } from "../ui/components";
import { CHECK, PURPOSE, RALLY_KIND, RALLY_STATUS_L, TOUR_STATUS_L, TRIP_STATUS_L, VEHICLE_KIND } from "./campaignText";

const istToday = () => new Date(Date.now() + 5.5 * 3600_000).toISOString().slice(0, 10);
const addDays = (d: string, n: number) => {
  const x = new Date(`${d}T00:00:00Z`);
  x.setUTCDate(x.getUTCDate() + n);
  return x.toISOString().slice(0, 10);
};
const istParts = (iso: string) => {
  const x = new Date(new Date(iso).getTime() + 5.5 * 3600_000).toISOString();
  return { date: x.slice(0, 10), time: x.slice(11, 16) };
};
const normMobile = (s: string) => s.replace(/[\s-]/g, "").replace(/^(\+91|91|0)(?=[6-9]\d{9}$)/, "");
const phoneOk = (s: string) => !s.trim() || /^[6-9]\d{9}$/.test(normMobile(s));
const boothOk = (s: string) => !s.trim() || /^\d{1,4}[A-Za-z]?$/.test(s.trim());
const intOrNull = (s: string) => (s.trim() === "" ? null : Math.max(0, Math.round(Number(s.replace(/[,\s₹]/g, "")) || 0)));
const dayLabel = (d: string, lang: "hi" | "en") =>
  new Date(`${d}T00:00:00+05:30`).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { weekday: "short", day: "numeric", month: "short", timeZone: "Asia/Kolkata" });

/** Load from the API; keeps the last data and the error apart so a failed refresh still shows the list. */
function useOnline<T>(load: () => Promise<T>, deps: unknown[]) {
  const { t, lang } = useApp();
  const [data, setData] = useState<T | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [n, setN] = useState(0);
  useEffect(() => {
    let off = false;
    setErr(null);
    load().then(
      (d) => !off && setData(d),
      (e) => !off && setErr((e as { status?: number })?.status ? apiErrorText(e, lang) ?? String(e) : t.netNeeded)
    );
    return () => {
      off = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, n]);
  return { data, err, reload: useCallback(() => setN((x) => x + 1), []) };
}

const saveError = (e: unknown, lang: "hi" | "en", net: string) => ((e as { status?: number })?.status ? apiErrorText(e, lang) ?? String(e) : net);

/* ------------------------------------------------------------------ hub */

export function CampaignHome() {
  const { app, t } = useApp();
  const parts = (["rallies", "tour", "vehicles"] as CampaignScreen[]).filter((p) => app.can(p));
  const icon: Record<CampaignScreen, string> = { rallies: "📣", tour: "🚩", vehicles: "🛺" };
  return (
    <div className="stack" data-testid="campaign">
      <div>
        <h2 style={{ margin: "0 0 4px" }}>{t.campTitle}</h2>
        <p className="muted" style={{ margin: 0 }}>
          {t.campExplain}
        </p>
      </div>
      {parts.map((p) => (
        <a key={p} href={href({ name: "camp", part: p })} className={`card card-link camp-tile camp-${p}`} data-testid={`camp-${p}`}>
          <span className="camp-ic" aria-hidden>
            {icon[p]}
          </span>
          <span>
            <b>{t[`camp_${p}` as const]}</b>
            <small>{t[`campSub_${p}` as const]}</small>
          </span>
          <span className="chev" aria-hidden>
            ›
          </span>
        </a>
      ))}
    </div>
  );
}

export function CampaignPart({ part }: { part: CampaignScreen }) {
  const { app, t } = useApp();
  if (!app.can(part)) return <Banner kind="warn">{t.notAllowedEdit}</Banner>;
  return part === "rallies" ? <Rallies /> : part === "tour" ? <Tour /> : <Vehicles />;
}

/* ------------------------------------------------------------------ rallies */

function Rallies() {
  const { app, t, lang } = useApp();
  const { data, err, reload } = useOnline(() => app.campaign<{ items: Rally[] }>("rallies"), [app]);
  const [view, setView] = useState<"up" | "past">("up");
  const [form, setForm] = useState<Rally | "new" | null>(null);
  // find a programme by its name, place, booth, panchayat or guest (N-PAC, 4 Nov 2026: "लोग ढूंढ सकें, edit कर सकें")
  const [q, setQ] = useState("");
  const now = Date.now() - 6 * 3600_000;
  const items = useMemo(() => {
    const all = data?.items ?? [];
    const up = all.filter((r) => r.status !== "done" && r.status !== "cancelled" && new Date(r.startsAt).getTime() >= now);
    const list = view === "up" ? up : all.filter((r) => !up.includes(r)).reverse();
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter((r) => [r.title, r.place, r.boothCode, r.panchayat, r.chiefGuest, r.inchargeName].some((x) => x && x.toLowerCase().includes(needle)));
  }, [data, view, now, q]);

  if (form) return <RallyForm rally={form === "new" ? null : form} onDone={(changed) => { setForm(null); if (changed) reload(); }} />;
  return (
    <div className="stack" data-testid="rallies">
      <h2 style={{ margin: 0 }}>📣 {t.camp_rallies}</h2>
      {err ? <Banner kind="error" action={{ label: t.reload, onClick: reload }}>{err}</Banner> : null}
      <Button big onClick={() => setForm("new")} testId="rally-new">
        {t.rNew}
      </Button>
      <Choice<"up" | "past"> name={t.camp_rallies} value={view} onChange={(v) => setView(v || "up")} options={[{ value: "up", label: t.rUpcoming }, { value: "past", label: t.rPast }]} />
      <input type="search" className="input" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.rSearch} aria-label={t.rSearch} data-testid="rally-search" />
      {data && !items.length ? <p className="muted">{q.trim() ? t.rNoMatch : t.rEmpty}</p> : null}
      {items.map((r) => {
        const d = istParts(r.startsAt);
        const done = RALLY_CHECKLIST.filter((k) => r.checklist?.[k]).length;
        return (
          <section key={r.id} className={`card camp-card rs-${r.status}`}>
            <div className="camp-when">
              {dayLabel(d.date, lang)} · {d.time}
            </div>
            <h3>{r.title}</h3>
            <div className="muted">
              📍 {r.place}
              {r.boothCode ? ` · ${t.boothNo} ${r.boothCode}` : ""}
            </div>
            <div className="tags">
              <span className="tag">{RALLY_KIND[r.kind][lang]}</span>
              <span className={`tag st-${r.status}`}>{RALLY_STATUS_L[r.status][lang]}</span>
              <span className="tag">{t.rChecklistN(done, RALLY_CHECKLIST.length)}</span>
            </div>
            <div className="meter" aria-hidden>
              <i style={{ width: `${(done / RALLY_CHECKLIST.length) * 100}%` }} />
            </div>
            {r.inchargeName || r.inchargePhone ? (
              <div className="muted">
                {t.rIncharge}: {r.inchargeName ?? ""} {r.inchargePhone ? <a href={`tel:+91${r.inchargePhone}`}>📞 {r.inchargePhone}</a> : null}
              </div>
            ) : null}
            <Button variant="secondary" onClick={() => setForm(r)}>
              {t.rEdit}
            </Button>
          </section>
        );
      })}
    </div>
  );
}

function RallyForm({ rally, onDone }: { rally: Rally | null; onDone: (changed: boolean) => void }) {
  const { app, t, lang } = useApp();
  const when = rally ? istParts(rally.startsAt) : { date: istToday(), time: "11:00" };
  const [f, setF] = useState({
    title: rally?.title ?? "",
    kind: (rally?.kind ?? "sabha") as RallyKind,
    date: when.date,
    time: when.time,
    place: rally?.place ?? "",
    booth: rally?.boothCode ?? "",
    expected: rally?.expectedCrowd != null ? String(rally.expectedCrowd) : "",
    actual: rally?.actualCrowd != null ? String(rally.actualCrowd) : "",
    cost: rally?.cost != null ? String(rally.cost) : "",
    incharge: rally?.inchargeName ?? "",
    phone: rally?.inchargePhone ?? "",
    status: (rally?.status ?? "planned") as RallyStatus,
    notes: rally?.notes ?? "",
  });
  const [checks, setChecks] = useState<Record<string, boolean>>({ ...(rally?.checklist ?? {}) });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const errs = {
    title: !f.title.trim() ? t.required : null,
    place: !f.place.trim() ? t.required : null,
    date: !/^\d{4}-\d{2}-\d{2}$/.test(f.date) ? t.required : null,
    time: !/^\d{2}:\d{2}$/.test(f.time) ? t.required : null,
    booth: boothOk(f.booth) ? null : t.boothCodeBad,
    phone: phoneOk(f.phone) ? null : t.phoneBad,
  };
  const save = async () => {
    setTouched(true);
    if (Object.values(errs).some(Boolean)) return;
    setBusy(true);
    setErr(null);
    try {
      const body = {
        title: f.title.trim(),
        kind: f.kind,
        startsAt: new Date(`${f.date}T${f.time}:00+05:30`).toISOString(),
        place: f.place.trim(),
        boothCode: f.booth.trim().toUpperCase() || null,
        expectedCrowd: intOrNull(f.expected),
        actualCrowd: intOrNull(f.actual),
        cost: intOrNull(f.cost),
        inchargeName: f.incharge.trim() || null,
        inchargePhone: f.phone.trim() ? normMobile(f.phone) : null,
        status: f.status,
        checklist: Object.fromEntries(RALLY_CHECKLIST.map((k) => [k, !!checks[k]])),
        notes: f.notes.trim() || null,
      };
      await app.campaignSave("rallies", body, rally?.id);
      onDone(true);
    } catch (e) {
      setErr(saveError(e, lang, t.netNeeded));
    } finally {
      setBusy(false);
    }
  };
  const show = (k: keyof typeof errs) => (touched ? errs[k] : null);
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-testid="rally-form"
    >
      <h2 style={{ margin: 0 }}>{rally ? t.rEdit : t.rNew.replace("＋ ", "")}</h2>
      <Field label={t.rTitle} name="r-title" value={f.title} onChange={set("title")} error={show("title")} maxLength={200} />
      <div className="field">
        <span className="label">{t.rKind}</span>
        <Choice<RallyKind> name={t.rKind} value={f.kind} onChange={(v) => set("kind")(v || "sabha")} options={RALLY_KINDS.map((k) => ({ value: k, label: RALLY_KIND[k][lang] }))} />
      </div>
      <div className="row2">
        <Field label={t.rDate} name="r-date" type="date" value={f.date} onChange={set("date")} error={show("date")} />
        <Field label={t.rTime} name="r-time" type="time" value={f.time} onChange={set("time")} error={show("time")} />
      </div>
      <Field label={t.rPlace} name="r-place" value={f.place} onChange={set("place")} error={show("place")} maxLength={200} />
      <div className="row2">
        <Field label={t.rBooth} name="r-booth" value={f.booth} onChange={set("booth")} error={show("booth")} maxLength={5} inputMode="numeric" />
        <NumberField label={t.rExpected} name="r-exp" value={f.expected} onChange={set("expected")} maxLength={7} />
      </div>
      <div className="row2">
        <Field label={t.rIncharge} name="r-inch" value={f.incharge} onChange={set("incharge")} maxLength={120} />
        <PhoneField label={t.rInchargePhone} name="r-phone" value={f.phone} onChange={set("phone")} error={show("phone")} />
      </div>
      <label className="field">
        <span className="label">{t.rStatus}</span>
        <select className="input" value={f.status} onChange={(e) => set("status")(e.target.value)}>
          {RALLY_STATUS.map((s) => (
            <option key={s} value={s}>
              {RALLY_STATUS_L[s][lang]}
            </option>
          ))}
        </select>
      </label>
      <fieldset className="checklist">
        <legend>
          {t.rChecklist} — {RALLY_CHECKLIST.filter((k) => checks[k]).length}/{RALLY_CHECKLIST.length}
        </legend>
        {RALLY_CHECKLIST.map((k) => (
          <Toggle key={k} label={CHECK[k][lang]} checked={!!checks[k]} onChange={(v) => setChecks((c) => ({ ...c, [k]: v }))} />
        ))}
      </fieldset>
      {rally ? (
        <div className="row2">
          <NumberField label={t.rActual} name="r-act" value={f.actual} onChange={set("actual")} maxLength={7} />
          <NumberField label={t.rCost} name="r-cost" value={f.cost} onChange={set("cost")} maxLength={9} />
        </div>
      ) : null}
      <Field label={t.rNotes} name="r-notes" value={f.notes} onChange={set("notes")} maxLength={1000} multiline />
      {err ? <Banner kind="error">{err}</Banner> : null}
      <div className="row2">
        <Button type="submit" big busy={busy} testId="rally-save">
          {t.save}
        </Button>
        <Button variant="secondary" big onClick={() => onDone(false)} disabled={busy}>
          {t.pCancel}
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ candidate tour */

function Tour() {
  const { app, t, lang } = useApp();
  const today = istToday();
  const [day, setDay] = useState(today);
  const { data, err, reload } = useOnline(() => app.campaign<{ items: TourStop[] }>("tour"), [app]);
  const [form, setForm] = useState<TourStop | "new" | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const days = useMemo(() => Array.from({ length: 10 }, (_, i) => addDays(today, i - 2)), [today]);
  const stops = useMemo(() => (data?.items ?? []).filter((s) => s.day === day).sort((a, b) => a.seq - b.seq || (a.atTime ?? "").localeCompare(b.atTime ?? "")), [data, day]);

  const mark = async (s: TourStop, status: TourStatus) => {
    setBusyId(s.id);
    setMsg(null);
    try {
      await app.campaignSave("tour", { status: s.status === status ? "planned" : status }, s.id);
      reload();
    } catch (e) {
      setMsg(saveError(e, lang, t.netNeeded));
    } finally {
      setBusyId(null);
    }
  };

  if (form) return <StopForm stop={form === "new" ? null : form} day={day} nextSeq={stops.length + 1} onDone={(changed) => { setForm(null); if (changed) reload(); }} />;
  const route = stops.length >= 2 ? `https://www.google.com/maps/dir/${stops.map((s) => encodeURIComponent(`${s.place}${s.panchayat ? `, ${s.panchayat}` : ""}`)).join("/")}` : null;
  return (
    <div className="stack" data-testid="tour">
      <h2 style={{ margin: 0 }}>🚩 {t.camp_tour}</h2>
      {err ? <Banner kind="error" action={{ label: t.reload, onClick: reload }}>{err}</Banner> : null}
      {msg ? <Banner kind="error">{msg}</Banner> : null}
      <div className="day-strip" role="tablist" aria-label={t.tDay}>
        {days.map((d) => {
          const n = (data?.items ?? []).filter((s) => s.day === d).length;
          return (
            <button key={d} type="button" role="tab" aria-selected={d === day} className={`day${d === day ? " on" : ""}${d === today ? " today" : ""}`} onClick={() => setDay(d)}>
              <span>{d === today ? t.tToday : dayLabel(d, lang)}</span>
              <small>{n || "—"}</small>
            </button>
          );
        })}
      </div>
      <Button big onClick={() => setForm("new")} testId="tour-new">
        {t.tAdd}
      </Button>
      {data && !stops.length ? <p className="muted">{t.tEmpty}</p> : null}
      <ol className="stops-m">
        {stops.map((s) => (
          <li key={s.id} className={`card stop-m ts-${s.status}`}>
            <div className="stop-top">
              <span className="seq">{s.status === "done" ? "✓" : s.seq}</span>
              <b>{s.atTime ?? "--:--"}</b>
              <span className="tag">{PURPOSE[s.purpose][lang]}</span>
              <span className={`tag ts-${s.status}`}>{TOUR_STATUS_L[s.status][lang]}</span>
            </div>
            <h3>{s.place}</h3>
            <div className="muted">
              {s.boothCode ? `${t.boothNo} ${s.boothCode}` : ""}
              {s.panchayat ? ` · ${s.panchayat}` : ""}
              {s.contactName ? ` · ${s.contactName}` : ""}
              {s.contactPhone ? (
                <>
                  {" "}
                  <a href={`tel:+91${s.contactPhone}`}>📞 {s.contactPhone}</a>
                </>
              ) : null}
            </div>
            {s.notes ? <div className="muted">{s.notes}</div> : null}
            <div className="mark3">
              {(["done", "delayed", "skipped"] as TourStatus[]).map((st) => (
                <button key={st} type="button" className={`mark m-${st}${s.status === st ? " on" : ""}`} aria-pressed={s.status === st} disabled={busyId === s.id} onClick={() => void mark(s, st)}>
                  {TOUR_STATUS_L[st][lang]}
                </button>
              ))}
              <button type="button" className="mark" onClick={() => setForm(s)}>
                ✎
              </button>
            </div>
          </li>
        ))}
      </ol>
      {route ? (
        <a className="btn btn-secondary" href={route} target="_blank" rel="noopener noreferrer">
          <span className="btn-text">🗺 {t.tRoute}</span>
        </a>
      ) : null}
    </div>
  );
}

function StopForm({ stop, day, nextSeq, onDone }: { stop: TourStop | null; day: string; nextSeq: number; onDone: (changed: boolean) => void }) {
  const { app, t, lang } = useApp();
  const [f, setF] = useState({
    day: stop?.day ?? day,
    time: stop?.atTime ?? "",
    seq: String(stop?.seq ?? nextSeq),
    place: stop?.place ?? "",
    booth: stop?.boothCode ?? "",
    purpose: (stop?.purpose ?? "door_to_door") as TourPurpose,
    contact: stop?.contactName ?? "",
    phone: stop?.contactPhone ?? "",
    notes: stop?.notes ?? "",
  });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const errs = { place: !f.place.trim() ? t.required : null, booth: boothOk(f.booth) ? null : t.boothCodeBad, phone: phoneOk(f.phone) ? null : t.phoneBad };
  const save = async () => {
    setTouched(true);
    if (Object.values(errs).some(Boolean)) return;
    setBusy(true);
    setErr(null);
    try {
      await app.campaignSave(
        "tour",
        {
          day: f.day,
          atTime: f.time || null,
          seq: Math.min(200, Math.max(1, Number(f.seq) || 1)),
          place: f.place.trim(),
          boothCode: f.booth.trim().toUpperCase() || null,
          purpose: f.purpose,
          contactName: f.contact.trim() || null,
          contactPhone: f.phone.trim() ? normMobile(f.phone) : null,
          notes: f.notes.trim() || null,
        },
        stop?.id
      );
      onDone(true);
    } catch (e) {
      setErr(saveError(e, lang, t.netNeeded));
    } finally {
      setBusy(false);
    }
  };
  const show = (k: keyof typeof errs) => (touched ? errs[k] : null);
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-testid="stop-form"
    >
      <h2 style={{ margin: 0 }}>{stop ? t.tEdit : t.tAdd.replace("＋ ", "")}</h2>
      <div className="row2">
        <Field label={t.tDay} name="s-day" type="date" value={f.day} onChange={set("day")} />
        <Field label={t.tTime} name="s-time" type="time" value={f.time} onChange={set("time")} />
      </div>
      <Field label={t.tPlace} name="s-place" value={f.place} onChange={set("place")} error={show("place")} maxLength={200} />
      <div className="row2">
        <Field label={t.tBooth} name="s-booth" value={f.booth} onChange={set("booth")} error={show("booth")} maxLength={5} inputMode="numeric" />
        <NumberField label={t.tSeq} name="s-seq" value={f.seq} onChange={set("seq")} maxLength={3} />
      </div>
      <div className="field">
        <span className="label">{t.tPurpose}</span>
        <Choice<TourPurpose> name={t.tPurpose} value={f.purpose} onChange={(v) => set("purpose")(v || "door_to_door")} options={TOUR_PURPOSES.map((p) => ({ value: p, label: PURPOSE[p][lang] }))} />
      </div>
      <div className="row2">
        <Field label={t.tContact} name="s-contact" value={f.contact} onChange={set("contact")} maxLength={120} />
        <PhoneField label={t.tContactPhone} name="s-phone" value={f.phone} onChange={set("phone")} error={show("phone")} />
      </div>
      <Field label={t.rNotes} name="s-notes" value={f.notes} onChange={set("notes")} maxLength={1000} multiline />
      {err ? <Banner kind="error">{err}</Banner> : null}
      <div className="row2">
        <Button type="submit" big busy={busy} testId="stop-save">
          {t.save}
        </Button>
        <Button variant="secondary" big onClick={() => onDone(false)} disabled={busy}>
          {t.pCancel}
        </Button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ campaign vehicles */

const plate = (reg: string) => {
  const m = reg.replace(/[\s-]/g, "").match(/^([A-Z]{2})(\d{1,2})([A-Z]{0,3})(\d{1,4})$/);
  return m ? [m[1], m[2], m[3], m[4]].filter(Boolean).join(" ") : reg;
};

/**
 * प्रचार गाड़ी has two options (N-PAC, 4 Nov 2026): "कर्मचारी के काम और डाटा भरने के लिए" — the fleet, trips and
 * fuel — and "ड्राइवर के लिए" — paste the driver link the system made; saving it starts the duty on this phone.
 */
function Vehicles() {
  const { app, t, lang } = useApp();
  const today = istToday();
  const { data, err, reload } = useOnline(() => app.campaign<{ items: Vehicle[] }>("vehicles", today), [app, today]);
  const [form, setForm] = useState<Vehicle | null>(null);
  const [who, setWho] = useState<"staff" | "driver">("staff");
  const [link, setLink] = useState("");
  const [bad, setBad] = useState(false);
  if (form) return <TripForm vehicle={form} day={today} onDone={(changed) => { setForm(null); if (changed) reload(); }} />;
  const list = (data?.items ?? []).filter((v) => v.status !== "released");
  const openLink = () => {
    const s = link.trim();
    const q = s.includes("?") ? s.slice(s.indexOf("?")).split("#")[0] : "";
    if (!parseAssemblyLink(q) || !parseDriveLink(q)) return setBad(true);
    if (!window.confirm(t.vDriverConfirm)) return;
    location.assign(`${location.origin}${location.pathname}${q}`);
  };
  if (who === "driver")
    return (
      <div className="stack" data-testid="vehicles-driver">
        <h2 style={{ margin: 0 }}>🛺 {t.camp_vehicles}</h2>
        <div className="chips" role="radiogroup">
          <button type="button" role="radio" aria-checked={false} className="chip" onClick={() => setWho("staff")} data-testid="veh-staff">
            👷 {t.vStaff}
          </button>
          <button type="button" role="radio" aria-checked className="chip chip-on" data-testid="veh-driver">
            🚐 {t.vDriverOpt}
          </button>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          {t.drvPasteHow}
        </p>
        <div className="card">
          <Field label={t.drvPasteLabel} name="veh-drive-link" value={link} onChange={(v) => { setLink(v); setBad(false); }} placeholder="https://…/?a=UP_396&drive=…" onEnter={openLink} error={bad ? t.drvPasteBad : undefined} />
          <Button onClick={openLink} disabled={!link.trim()} testId="veh-drive-open">
            {t.drvPasteOpen}
          </Button>
        </div>
        <p className="muted" style={{ margin: 0 }}>
          {t.vDriverNote}
        </p>
      </div>
    );
  return (
    <div className="stack" data-testid="vehicles">
      <h2 style={{ margin: 0 }}>🛺 {t.camp_vehicles}</h2>
      <div className="chips" role="radiogroup">
        <button type="button" role="radio" aria-checked className="chip chip-on" data-testid="veh-staff">
          👷 {t.vStaff}
        </button>
        <button type="button" role="radio" aria-checked={false} className="chip" onClick={() => setWho("driver")} data-testid="veh-driver">
          🚐 {t.vDriverOpt}
        </button>
      </div>
      {err ? <Banner kind="error" action={{ label: t.reload, onClick: reload }}>{err}</Banner> : null}
      {data && !list.length ? <p className="muted">{t.vEmpty}</p> : null}
      {list.map((v) => {
        const tr = v.today;
        const km = tr && tr.endKm != null && tr.startKm != null ? tr.endKm - tr.startKm : null;
        return (
          <section key={v.id} className={`card camp-card vs-${v.status}`}>
            <div className="veh-top">
              <span className="plate-m">
                <i>IND</i>
                {plate(v.regNo)}
              </span>
              <span className="tag">{VEHICLE_KIND[v.kind][lang]}</span>
            </div>
            {v.driverName || v.driverPhone ? (
              <div className="muted">
                {t.vDriver}: {v.driverName ?? ""} {v.driverPhone ? <a href={`tel:+91${v.driverPhone}`}>📞 {v.driverPhone}</a> : null}
              </div>
            ) : null}
            <div className={`veh-today${tr ? ` tr-${tr.status}` : ""}`}>
              <small>{t.vToday}</small>
              {tr ? (
                <>
                  <b>{tr.area}</b>
                  {tr.boothCodes.length ? <span className="muted">{t.boothNo}: {tr.boothCodes.join(", ")}</span> : null}
                  <span className="tags">
                    <span className={`tag tr-${tr.status}`}>{TRIP_STATUS_L[tr.status][lang]}</span>
                    {km != null ? <span className="tag">{t.vKm(km)}</span> : null}
                  </span>
                </>
              ) : (
                <span className="muted">{t.vNoTrip}</span>
              )}
            </div>
            <Button variant={tr ? "secondary" : "primary"} onClick={() => setForm(v)}>
              {tr ? t.vEditTrip : t.vSetTrip}
            </Button>
          </section>
        );
      })}
    </div>
  );
}

function TripForm({ vehicle, day, onDone }: { vehicle: Vehicle; day: string; onDone: (changed: boolean) => void }) {
  const { app, t, lang } = useApp();
  const tr: Trip | null = vehicle.today;
  const [f, setF] = useState({
    area: tr?.area ?? "",
    booths: tr?.boothCodes.join(", ") ?? "",
    startKm: tr?.startKm != null ? String(tr.startKm) : "",
    endKm: tr?.endKm != null ? String(tr.endKm) : "",
    fuelL: tr?.fuelLitres != null ? String(tr.fuelLitres) : "",
    fuelCost: tr?.fuelCost != null ? String(tr.fuelCost) : "",
    cashGiven: tr?.cashGiven != null ? String(tr.cashGiven) : "",
    status: (tr?.status ?? "running") as TripStatus,
  });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }));
  const codes = f.booths.split(/[\s,]+/).map((x) => x.trim().toUpperCase()).filter(Boolean);
  const sk = intOrNull(f.startKm);
  const ek = intOrNull(f.endKm);
  const errs = { area: !f.area.trim() ? t.required : null, booths: codes.some((c) => !/^\d{1,4}[A-Z]?$/.test(c)) ? t.boothCodeBad : null, km: sk != null && ek != null && ek < sk ? t.vKmBad : null };
  const save = async () => {
    setTouched(true);
    if (Object.values(errs).some(Boolean)) return;
    setBusy(true);
    setErr(null);
    try {
      const litres = f.fuelL.trim() === "" ? null : Math.max(0, Number(f.fuelL.replace(",", ".")) || 0);
      await app.campaignSave(
        "trips",
        { vehicleId: vehicle.id, day: tr?.day ?? day, area: f.area.trim(), boothCodes: codes, startKm: sk, endKm: ek, fuelLitres: litres, fuelCost: intOrNull(f.fuelCost), cashGiven: intOrNull(f.cashGiven), status: f.status },
        tr?.id
      );
      onDone(true);
    } catch (e) {
      setErr(saveError(e, lang, t.netNeeded));
    } finally {
      setBusy(false);
    }
  };
  const show = (k: keyof typeof errs) => (touched ? errs[k] : null);
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
      data-testid="trip-form"
    >
      <h2 style={{ margin: 0 }}>
        {t.vToday} · {plate(vehicle.regNo)}
      </h2>
      <Field label={t.vArea} name="t-area" value={f.area} onChange={set("area")} error={show("area")} maxLength={300} />
      <Field label={t.vBooths} name="t-booths" value={f.booths} onChange={set("booths")} error={show("booths")} hint={t.vBoothsHint} />
      <div className="row2">
        <NumberField label={t.vStartKm} name="t-sk" value={f.startKm} onChange={set("startKm")} maxLength={7} />
        <NumberField label={t.vEndKm} name="t-ek" value={f.endKm} onChange={set("endKm")} maxLength={7} error={show("km")} />
      </div>
      <div className="row2">
        <Field label={t.vFuelL} name="t-fl" value={f.fuelL} onChange={set("fuelL")} inputMode="decimal" maxLength={6} />
        <NumberField label={t.vFuelCost} name="t-fc" value={f.fuelCost} onChange={set("fuelCost")} maxLength={7} />
      </div>
      <NumberField label={t.vCash} name="t-cash" value={f.cashGiven} onChange={set("cashGiven")} maxLength={7} hint={t.vCashHint} />
      <div className="field">
        <span className="label">{t.rStatus}</span>
        <Choice<TripStatus> name={t.rStatus} value={f.status} onChange={(v) => set("status")(v || "running")} options={TRIP_STATUS.map((s) => ({ value: s, label: TRIP_STATUS_L[s][lang] }))} />
      </div>
      {err ? <Banner kind="error">{err}</Banner> : null}
      <div className="row2">
        <Button type="submit" big busy={busy} testId="trip-save">
          {t.save}
        </Button>
        <Button variant="secondary" big onClick={() => onDone(false)} disabled={busy}>
          {t.pCancel}
        </Button>
      </div>
    </form>
  );
}
